;(function () {
  const SELECTORS = {
    popupBanner: '.cky-consent-container',
    notice: '[data-cky-tag="notice"]',
    title: '[data-cky-tag="title"]',
    preferenceModal: '.cky-modal.cky-modal-open',
    preferenceCenter: '.cky-preference-center',
    preferenceDetail: '[data-cky-tag="detail"]',
    overlay: '.cky-overlay',
    settingsButton: '[data-cky-tag="settings-button"]',
    revisitButton: '[data-cky-tag="revisit-consent"]',
    closePreferenceButtons: [
      '[data-cky-tag="detail-close"]',
      '[data-cky-tag="optout-close"]',
      '[data-cky-tag="optout-cancel-button"]',
    ].join(','),
    closeBannerButton: '[data-cky-tag="close-button"]',
  }

  const state = {
    activeTrap: null,
    lastTrigger: null,
  }

  function setAttributeIfChanged(element, name, value) {
    if (!element) return
    if (element.getAttribute(name) === value) return
    element.setAttribute(name, value)
  }

  function removeAttributeIfPresent(element, name) {
    if (!element) return
    if (!element.hasAttribute(name)) return
    element.removeAttribute(name)
  }

  function setManagedTabbableState(container, enabled) {
    if (!container) return

    const elements = container.querySelectorAll(
      'a[href], button, input:not([type="hidden"]), select, textarea, [tabindex]',
    )

    elements.forEach((element) => {
      if (enabled) {
        if (element.dataset.fedlusTabindexManaged !== 'true') return

        const original = element.dataset.fedlusOriginalTabindex
        if (original === '__none__') {
          removeAttributeIfPresent(element, 'tabindex')
        } else if (typeof original === 'string') {
          setAttributeIfChanged(element, 'tabindex', original)
        }

        delete element.dataset.fedlusOriginalTabindex
        delete element.dataset.fedlusTabindexManaged
        return
      }

      if (element.dataset.fedlusTabindexManaged === 'true') return

      const currentTabindex = element.getAttribute('tabindex')
      element.dataset.fedlusTabindexManaged = 'true'
      element.dataset.fedlusOriginalTabindex =
        currentTabindex === null ? '__none__' : currentTabindex
      setAttributeIfChanged(element, 'tabindex', '-1')
    })
  }

  function isVisible(element) {
    if (!element) return false

    return (
      !element.classList.contains('cky-hide') &&
      element.getAttribute('aria-hidden') !== 'true' &&
      (element.offsetWidth > 0 ||
        element.offsetHeight > 0 ||
        element.getClientRects().length > 0)
    )
  }

  function isOpenPreferenceModal(element) {
    if (!element) return false

    return (
      element.classList.contains('cky-modal-open') &&
      !element.classList.contains('cky-hide')
    )
  }

  function getFocusableElements(container) {
    if (!container) return []

    const selectors = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled]):not([type="hidden"])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
    ]

    return Array.from(container.querySelectorAll(selectors.join(','))).filter(
      (element) => {
        if (!isVisible(element)) return false
        if (element.getAttribute('aria-hidden') === 'true') return false
        return true
      },
    )
  }

  function getInitialFocusTarget(container) {
    if (!container) return null

    return (
      container.querySelector(SELECTORS.closePreferenceButtons) ||
      container.querySelector(SELECTORS.closeBannerButton) ||
      getFocusableElements(container)[0] ||
      container
    )
  }

  function moveFocusIntoContainer(container) {
    if (!container) return

    const initialFocus = getInitialFocusTarget(container)
    if (!initialFocus) return

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        if (!container.contains(document.activeElement)) {
          initialFocus.focus()
        }
      })
    })
  }

  function ensureId(element, fallback) {
    if (!element) return null
    if (!element.id) {
      element.id = fallback
    }
    return element.id
  }

  function getClosestMatch(target, selector) {
    if (!target) return null

    if (target instanceof Element) {
      return target.closest(selector)
    }

    if (target.parentElement instanceof Element) {
      return target.parentElement.closest(selector)
    }

    return null
  }

  function getPopupBannerContainer() {
    const notice = document.querySelector(SELECTORS.notice)
    if (!isVisible(notice)) return null

    const container = notice.closest(SELECTORS.popupBanner)
    if (!container || !isVisible(container)) return null

    const className = container.className || ''
    if (!className.includes('cky-popup')) return null

    return container
  }

  function getOpenPreferenceContainer() {
    const modals = document.querySelectorAll('.cky-modal')

    for (const modal of modals) {
      if (isOpenPreferenceModal(modal)) {
        return modal
      }
    }

    return null
  }

  function applyBannerAttributes(container, titleText) {
    if (!container) return

    setManagedTabbableState(container, true)
    setAttributeIfChanged(container, 'role', 'region')
    removeAttributeIfPresent(container, 'aria-modal')
    removeAttributeIfPresent(container, 'aria-hidden')
    removeAttributeIfPresent(container, 'inert')
    removeAttributeIfPresent(container, 'tabindex')

    const titleElement =
      container.querySelector(SELECTORS.title) ||
      container.querySelector('h1, h2, h3, h4, h5, h6')

    if (titleElement) {
      const id = ensureId(titleElement, 'cky-cookie-banner-title')
      setAttributeIfChanged(container, 'aria-labelledby', id)
      removeAttributeIfPresent(container, 'aria-label')
    } else if (titleText) {
      setAttributeIfChanged(container, 'aria-label', titleText)
      removeAttributeIfPresent(container, 'aria-labelledby')
    }
  }

  function applyDialogAttributes(container, titleText) {
    if (!container) return

    setManagedTabbableState(container, true)
    removeAttributeIfPresent(container, 'aria-hidden')
    removeAttributeIfPresent(container, 'inert')
    setAttributeIfChanged(container, 'role', 'dialog')
    setAttributeIfChanged(container, 'aria-modal', 'true')
    setAttributeIfChanged(container, 'tabindex', '-1')

    const preferenceCenter = container.querySelector(SELECTORS.preferenceCenter)
    if (preferenceCenter && preferenceCenter !== container) {
      removeAttributeIfPresent(preferenceCenter, 'role')
      removeAttributeIfPresent(preferenceCenter, 'aria-modal')
      removeAttributeIfPresent(preferenceCenter, 'aria-labelledby')
      removeAttributeIfPresent(preferenceCenter, 'aria-label')
      removeAttributeIfPresent(preferenceCenter, 'tabindex')
    }

    const titleElement =
      container.querySelector(SELECTORS.title) ||
      container.querySelector('h1, h2, h3, h4, h5, h6')

    if (titleElement) {
      const id = ensureId(
        titleElement,
        titleText === 'cookie-banner-title'
          ? 'cky-cookie-banner-title'
          : 'cky-cookie-preference-title',
      )
      setAttributeIfChanged(container, 'aria-labelledby', id)
      removeAttributeIfPresent(container, 'aria-label')
    } else if (titleText) {
      setAttributeIfChanged(container, 'aria-label', titleText)
      removeAttributeIfPresent(container, 'aria-labelledby')
    }
  }

  function updateSemantics() {
    sanitizeHiddenElements()

    const popupBanner = getPopupBannerContainer()
    if (popupBanner) {
      applyBannerAttributes(popupBanner, 'Preferenze cookie')
    }

    const preference = getOpenPreferenceContainer()
    if (preference) {
      applyDialogAttributes(preference, 'Personalizza preferenze cookie')
    }

    document
      .querySelectorAll(
        `${SELECTORS.closePreferenceButtons}, ${SELECTORS.closeBannerButton}`,
      )
      .forEach((button) => {
        if (!button.getAttribute('aria-label') && !button.textContent.trim()) {
          setAttributeIfChanged(button, 'aria-label', 'Chiudi preferenze cookie')
        }
      })

    document
      .querySelectorAll(
        `${SELECTORS.settingsButton}, ${SELECTORS.revisitButton}`,
      )
      .forEach((button) => {
        setAttributeIfChanged(button, 'aria-haspopup', 'dialog')
      })

    enhanceCategorySwitches()
  }

  function sanitizeHiddenElements() {
    document.querySelectorAll(SELECTORS.popupBanner).forEach((banner) => {
      if (isVisible(banner)) {
        setManagedTabbableState(banner, true)
        return
      }

      setManagedTabbableState(banner, false)
      removeAttributeIfPresent(banner, 'role')
      removeAttributeIfPresent(banner, 'aria-modal')
      removeAttributeIfPresent(banner, 'aria-labelledby')
      removeAttributeIfPresent(banner, 'aria-label')
      removeAttributeIfPresent(banner, 'tabindex')
    })

    document.querySelectorAll('.cky-modal').forEach((modal) => {
      const isOpen = isOpenPreferenceModal(modal)

      if (isOpen) {
        setManagedTabbableState(modal, true)
        removeAttributeIfPresent(modal, 'aria-hidden')
        removeAttributeIfPresent(modal, 'inert')
        return
      }

      setManagedTabbableState(modal, false)
      setAttributeIfChanged(modal, 'aria-hidden', 'true')
      if (!modal.hasAttribute('inert')) {
        modal.setAttribute('inert', '')
      }
      setAttributeIfChanged(modal, 'tabindex', '-1')
      removeAttributeIfPresent(modal, 'role')
      removeAttributeIfPresent(modal, 'aria-modal')
      removeAttributeIfPresent(modal, 'aria-labelledby')
      removeAttributeIfPresent(modal, 'aria-label')

      const preferenceCenter = modal.querySelector(SELECTORS.preferenceCenter)
      if (!preferenceCenter) return

      removeAttributeIfPresent(preferenceCenter, 'role')
      removeAttributeIfPresent(preferenceCenter, 'aria-modal')
      removeAttributeIfPresent(preferenceCenter, 'aria-labelledby')
      removeAttributeIfPresent(preferenceCenter, 'aria-label')
      removeAttributeIfPresent(preferenceCenter, 'tabindex')
    })
  }

  function enhanceCategorySwitches() {
    const accordionItems = document.querySelectorAll('.cky-accordion')

    accordionItems.forEach((accordion, index) => {
      const titleButton = accordion.querySelector('[data-cky-tag="detail-category-title"]')
      const description = accordion.querySelector('[data-cky-tag="detail-category-description"]')
      const checkbox = accordion.querySelector('.cky-switch input[type="checkbox"]')

      if (!titleButton || !checkbox) return

      const titleId = ensureId(
        titleButton,
        accordion.id ? `${accordion.id}-label` : `cky-category-label-${index + 1}`,
      )

      setAttributeIfChanged(checkbox, 'role', 'switch')
      setAttributeIfChanged(checkbox, 'aria-labelledby', titleId)
      setAttributeIfChanged(
        checkbox,
        'aria-checked',
        checkbox.checked ? 'true' : 'false',
      )

      if (description) {
        const descriptionId = ensureId(
          description,
          accordion.id
            ? `${accordion.id}-description`
            : `cky-category-description-${index + 1}`,
        )
        setAttributeIfChanged(checkbox, 'aria-describedby', descriptionId)
      }

      if (checkbox.dataset.fedlusA11yBound === 'true') return

      checkbox.addEventListener('change', () => {
        setAttributeIfChanged(
          checkbox,
          'aria-checked',
          checkbox.checked ? 'true' : 'false',
        )
      })

      checkbox.dataset.fedlusA11yBound = 'true'
    })
  }

  function activateTrap(container) {
    if (!container) return

    if (state.activeTrap === container) {
      if (!container.contains(document.activeElement)) {
        moveFocusIntoContainer(container)
      }
      return
    }

    state.activeTrap = container
    moveFocusIntoContainer(container)
  }

  function deactivateTrap() {
    const trigger = state.lastTrigger
    state.activeTrap = null

    if (trigger && typeof trigger.focus === 'function' && document.contains(trigger)) {
      window.setTimeout(() => {
        trigger.focus()
      }, 0)
    }
  }

  function closeActiveLayer() {
    const preference = getOpenPreferenceContainer()
    if (preference) {
      const closeButton =
        preference.querySelector(SELECTORS.closePreferenceButtons) ||
        document.querySelector(SELECTORS.closePreferenceButtons)

      if (closeButton) {
        closeButton.click()
      }
      return
    }

    const banner = getPopupBannerContainer()
    if (!banner) return

    const closeButton = banner.querySelector(SELECTORS.closeBannerButton)
    if (closeButton) {
      closeButton.click()
    }
  }

  function trapFocus(event) {
    const container = state.activeTrap
    if (!container || event.key !== 'Tab') return

    const focusableElements = getFocusableElements(container)
    if (focusableElements.length === 0) {
      event.preventDefault()
      return
    }

    const first = focusableElements[0]
    const last = focusableElements[focusableElements.length - 1]

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault()
      last.focus()
      return
    }

    if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first.focus()
    }
  }

  function handleDocumentKeydown(event) {
    if (event.key === 'Escape') {
      if (state.activeTrap) {
        event.preventDefault()
        closeActiveLayer()
      }
      return
    }

    trapFocus(event)
  }

  function syncTrapState() {
    updateSemantics()

    const preference = getOpenPreferenceContainer()
    if (preference) {
      activateTrap(preference)
      return
    }

    if (state.activeTrap) {
      deactivateTrap()
    }
  }

  function rememberTrigger(event) {
    const trigger = getClosestMatch(
      event.target,
      `${SELECTORS.settingsButton}, ${SELECTORS.revisitButton}`,
    )

    if (trigger) {
      state.lastTrigger = trigger
    }
  }

  function observeCookieyes() {
    const observer = new MutationObserver(() => {
      syncTrapState()
    })

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style', 'aria-hidden'],
    })
  }

  function init() {
    document.addEventListener('click', rememberTrigger, true)
    document.addEventListener('keydown', handleDocumentKeydown, true)
    observeCookieyes()
    syncTrapState()
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init)
  } else {
    init()
  }
})()
